import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PromoCodesTab = ({ authToken }) => {
  const [codes, setCodes] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    code: '',
    video_id: '',
    discount_percentage: 100,
    max_uses: 3,
    expires_at: ''
  });

  const headers = { Authorization: `Bearer ${authToken}` };

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/promo-codes/admin/list`, { headers });
      setCodes(res.data.promo_codes || []);
    } catch (err) {
      console.error('Failed to fetch promo codes:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/vod/videos?limit=200`);
      setVideos(res.data.videos || []);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
    fetchVideos();
  }, [fetchCodes, fetchVideos]);

  const resetForm = () => {
    setForm({ code: '', video_id: '', discount_percentage: 100, max_uses: 3, expires_at: '' });
    setEditingCode(null);
    setShowForm(false);
    setError('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.video_id) { setError('Please select a video'); return; }
    if (!form.expires_at) { setError('Please set an expiry date'); return; }

    try {
      const payload = {
        video_id: form.video_id,
        discount_percentage: parseInt(form.discount_percentage),
        max_uses: parseInt(form.max_uses),
        expires_at: new Date(form.expires_at).toISOString()
      };
      if (form.code.trim()) payload.code = form.code.trim();

      await axios.post(`${API}/api/promo-codes/admin/create`, payload, { headers });
      setSuccess('Promo code created successfully!');
      resetForm();
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = {};
      if (form.discount_percentage !== editingCode.discount_percentage)
        payload.discount_percentage = parseInt(form.discount_percentage);
      if (form.max_uses !== editingCode.max_uses)
        payload.max_uses = parseInt(form.max_uses);
      if (form.expires_at)
        payload.expires_at = new Date(form.expires_at).toISOString();

      await axios.put(`${API}/api/promo-codes/admin/${editingCode.id}`, payload, { headers });
      setSuccess('Promo code updated!');
      resetForm();
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/api/promo-codes/admin/${id}`, { headers });
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggle = async (code) => {
    try {
      await axios.put(`${API}/api/promo-codes/admin/${code.id}`, { is_active: !code.is_active }, { headers });
      fetchCodes();
    } catch (err) {
      setError('Failed to toggle');
    }
  };

  const startEdit = (code) => {
    setEditingCode(code);
    const expiresDate = code.expires_at ? new Date(code.expires_at).toISOString().slice(0, 16) : '';
    setForm({
      code: code.code,
      video_id: code.video_id,
      discount_percentage: code.discount_percentage,
      max_uses: code.max_uses,
      expires_at: expiresDate
    });
    setShowForm(true);
    setError('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(`Copied "${text}" to clipboard!`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const isExpired = (dateStr) => {
    try { return new Date(dateStr) < new Date(); } catch { return false; }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="promo-codes-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Promo Codes</h2>
          <p className="text-sm text-gray-400 mt-1">{codes.length} code{codes.length !== 1 ? 's' : ''} created</p>
        </div>
        <button
          data-testid="create-promo-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          + New Promo Code
        </button>
      </div>

      {/* Messages */}
      {error && <div className="p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300 text-sm">{error}</div>}
      {success && <div className="p-3 bg-emerald-500/20 border border-emerald-500/40 rounded-lg text-emerald-300 text-sm">{success}</div>}

      {/* Create / Edit Form */}
      {showForm && (
        <form
          data-testid="promo-form"
          onSubmit={editingCode ? handleUpdate : handleCreate}
          className="p-5 bg-gray-800/60 border border-gray-700 rounded-xl space-y-4"
        >
          <h3 className="text-base font-semibold text-white">{editingCode ? 'Edit Promo Code' : 'Create Promo Code'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Code {!editingCode && '(leave blank to auto-generate)'}</label>
              <input
                data-testid="promo-code-input"
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                disabled={!!editingCode}
                placeholder="e.g. SUMMER50"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
              />
            </div>

            {/* Video Selection */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Video *</label>
              <select
                data-testid="promo-video-select"
                value={form.video_id}
                onChange={(e) => setForm({ ...form, video_id: e.target.value })}
                disabled={!!editingCode}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select a video...</option>
                {videos.map(v => (
                  <option key={v.id} value={v.id}>{v.title} {v.price > 0 ? `(£${v.price})` : '(Free)'}</option>
                ))}
              </select>
            </div>

            {/* Discount % */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Discount Percentage *</label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="promo-discount-input"
                  type="number"
                  min="0"
                  max="100"
                  value={form.discount_percentage}
                  onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })}
                  className="w-24 px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                />
                <span className="text-gray-400 text-sm">%</span>
                <div className="flex gap-1 ml-2">
                  {[10, 20, 50, 100].map(pct => (
                    <button
                      key={pct}
                      type="button"
                      onClick={() => setForm({ ...form, discount_percentage: pct })}
                      className={`px-2 py-1 text-xs rounded ${parseInt(form.discount_percentage) === pct ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                    >
                      {pct === 100 ? 'FREE' : `${pct}%`}
                    </button>
                  ))}
                </div>
              </div>
              {parseInt(form.discount_percentage) === 100 && (
                <p className="text-xs text-emerald-400 mt-1">This code will grant free access to the video</p>
              )}
            </div>

            {/* Max Uses */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Max Uses *</label>
              <input
                data-testid="promo-max-uses-input"
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Expiry */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1">Expires At *</label>
              <input
                data-testid="promo-expires-input"
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              data-testid="promo-submit-btn"
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {editingCode ? 'Update' : 'Create Code'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Codes List */}
      {codes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No promo codes yet</p>
          <p className="text-sm mt-1">Create your first promo code above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {codes.map(code => {
            const expired = isExpired(code.expires_at);
            const maxedOut = code.current_uses >= code.max_uses;
            const inactive = !code.is_active;
            const statusColor = expired ? 'text-red-400' : maxedOut ? 'text-amber-400' : inactive ? 'text-gray-500' : 'text-emerald-400';
            const statusText = expired ? 'Expired' : maxedOut ? 'Limit Reached' : inactive ? 'Disabled' : 'Active';

            return (
              <div
                key={code.id}
                data-testid={`promo-card-${code.code}`}
                className={`p-4 bg-gray-800/50 border rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${inactive || expired ? 'border-gray-700/50 opacity-60' : 'border-gray-700'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      onClick={() => copyToClipboard(code.code)}
                      className="font-mono text-base font-bold text-white bg-gray-900 px-3 py-1 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer"
                      title="Click to copy"
                    >
                      {code.code}
                    </button>
                    <span className={`text-xs font-medium ${code.discount_percentage >= 100 ? 'text-emerald-400' : 'text-blue-400'}`}>
                      {code.discount_percentage >= 100 ? 'FREE' : `${code.discount_percentage}% OFF`}
                    </span>
                    <span className={`text-xs font-medium ${statusColor}`}>{statusText}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                    <span>Video: <span className="text-gray-300">{code.video_title || 'Unknown'}</span></span>
                    <span>Uses: <span className="text-gray-300">{code.current_uses}/{code.max_uses}</span></span>
                    <span>Expires: <span className="text-gray-300">{new Date(code.expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleToggle(code)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${code.is_active ? 'bg-amber-600/20 text-amber-400 hover:bg-amber-600/30' : 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'}`}
                  >
                    {code.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => startEdit(code)}
                    className="px-3 py-1.5 text-xs rounded-lg font-medium bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(code.id)}
                    className="px-3 py-1.5 text-xs rounded-lg font-medium bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoCodesTab;
